'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';

import { useAuth } from '@/entities/user';

import {
	CAREER_OFFER_BANNER_CAMPAIGN,
	CAREER_OFFER_BANNER_PLACEMENT,
	isCareerOfferBannerArrival,
} from '../model/banner-campaign';
import { CAREER_OFFER_EVENTS, CAREER_OFFER_GOALS } from '../model/events';
import type {
	CareerOfferTechnology,
	CareerOfferVisitor,
} from '../model/events';
import { getCareerOfferRouteContext } from '../model/route-context';
import { useCareerOfferAnalytics } from './use-career-offer-analytics';

type CareerOfferPageEventProps = {
	kind: 'selector' | 'landing' | 'unavailable';
	technology?: CareerOfferTechnology;
	visitor?: CareerOfferVisitor;
};

const pageEventNames = {
	selector: CAREER_OFFER_EVENTS.SELECTOR_VIEW,
	landing: CAREER_OFFER_EVENTS.LANDING_VIEW,
	unavailable: CAREER_OFFER_EVENTS.UNAVAILABLE_VIEW,
} as const;

export function CareerOfferPageEvent({
	kind,
	technology,
	visitor,
}: CareerOfferPageEventProps) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { isAuthorized, loading } = useAuth();
	const { trackEvent, trackGoal, trackHit } = useCareerOfferAnalytics();
	const trackedKey = useRef<string | null>(null);
	const query = searchParams.toString();
	const routeContext = useMemo(
		() => getCareerOfferRouteContext(new URLSearchParams(query)),
		[query],
	);
	const bannerArrival =
		kind === 'selector' &&
		isCareerOfferBannerArrival(new URLSearchParams(query));
	const effectiveVisitor =
		visitor ??
		(loading ? undefined : isAuthorized ? 'authenticated' : 'anonymous');

	useEffect(() => {
		if (!effectiveVisitor) return;
		const key = `${pathname}:${kind}:${technology ?? ''}:${routeContext.routing}:${kind === 'selector' ? query : ''}`;
		if (trackedKey.current === key) return;
		trackedKey.current = key;

		const eventContext = {
			...(technology && { technology }),
			visitor: effectiveVisitor,
			...routeContext,
		};
		if (
			kind === 'landing' &&
			routeContext.routing === 'primary_language_auto'
		) {
			trackEvent({ name: CAREER_OFFER_EVENTS.AUTO_REDIRECT, ...eventContext });
		}
		if (bannerArrival) {
			// The installed Metrica wrapper listens to Pages Router events only.
			trackHit(window.location.href);
		}
		trackEvent({ name: pageEventNames[kind], ...eventContext });
		if (bannerArrival) {
			trackGoal(CAREER_OFFER_GOALS.BANNER_ARRIVAL, {
				name: CAREER_OFFER_EVENTS.SELECTOR_VIEW,
				...eventContext,
				placement: CAREER_OFFER_BANNER_PLACEMENT,
				campaign: CAREER_OFFER_BANNER_CAMPAIGN,
				creative: new URLSearchParams(query).get('utm_content') ?? undefined,
			});
		}
	}, [
		bannerArrival,
		effectiveVisitor,
		kind,
		pathname,
		query,
		routeContext,
		technology,
		trackEvent,
		trackGoal,
		trackHit,
	]);

	return null;
}
